import { useState, useEffect } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import Sidebar from './Sidebar';
import BottomNavigation from './mobile/BottomNavigation';
import ThemeToggle from './ThemeToggle';

function useScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
}

function useDashboardTheme() {
  useEffect(() => {
    // Apply saved preference when entering the dashboard (coming from public pages)
    const saved = localStorage.getItem('pulse-theme');
    if (saved === 'dark') {
      document.documentElement.setAttribute('data-theme', 'dark');
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.removeAttribute('data-theme');
      document.documentElement.classList.remove('dark');
    }
  }, []);
}

const COLLAPSED_WIDTH = 76;
const EXPANDED_WIDTH = 300;

export default function Layout() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarExpanded, setSidebarExpanded] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  useScrollToTop();
  useDashboardTheme();

  const isDashboardHome = location.pathname === '/dashboard' || location.pathname === '/dashboard/';
  if (isDashboardHome) {
    return <Outlet />;
  }

  const isSettings = location.pathname.startsWith('/dashboard/settings');

  return (
    <div className="min-h-screen flex" style={{ background: 'var(--bg-deep)', color: 'var(--text-body)' }}>
      <Sidebar
        isOpen={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
        onExpandChange={setSidebarExpanded}
      />

      <motion.div
        className="flex-1 flex flex-col layout-main"
        animate={{ marginLeft: sidebarExpanded ? EXPANDED_WIDTH : COLLAPSED_WIDTH }}
        transition={{ duration: 0.18, ease: [0.4, 0, 0.2, 1] }}
        style={{ marginLeft: COLLAPSED_WIDTH }}
      >
        {/* Top-right controls — always visible */}
        <div
          className="sticky top-0 z-30 flex justify-end items-center gap-2 px-4 h-12"
          style={{ background: 'var(--bg-deep)', borderBottom: '1px solid var(--border-base)' }}
        >
          <ThemeToggle />
          <button
            onClick={() => navigate('/dashboard/settings')}
            className="p-2 rounded-lg transition-colors"
            style={{ color: 'var(--text-secondary)', background: 'transparent' }}
            onMouseEnter={(e) => { e.currentTarget.style.color = 'var(--cyan)'; e.currentTarget.style.background = 'var(--nav-hover-bg)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.color = 'var(--text-secondary)'; e.currentTarget.style.background = 'transparent'; }}
            aria-label="Settings"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
          </button>
        </div>

        <main
          key={location.pathname}
          className="flex-1 overflow-auto pb-[72px] lg:pb-0 page-enter"
          style={{ background: 'var(--bg-deep)' }}
        >
          {isSettings ? (
            <Outlet />
          ) : (
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
              <Outlet />
            </div>
          )}
        </main>

        <BottomNavigation />
      </motion.div>
    </div>
  );
}
